import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { ConsoleDashboard, DashboardKind } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * «Gestione cruscotti»: tutti i cruscotti di Eagle (e le
 * dashboard della home, per chi le vede) in una tabella sola — di chi sono,
 * quante sezioni e widget, chi li ha toccati per ultimo — con le azioni che
 * prima stavano sparse o non c'erano: **Apri** nell'editor, **A parete**
 * (quello stesso cruscotto, a tutto schermo) e, nel menu «⋯», **Duplica**,
 * **Rinomina**, **Condividi con il tenant** / **Rendi mia**, **Elimina**. I
 * link per i monitor stanno in «Sinottici pubblicati».
 *
 * Nasce da una segnalazione: «personalizzo il cruscotto, vedo "Sinottico a
 * parete", mi aspetto che metta a parete quello che vedo, e invece apre una
 * roba diversa definita altrove». Qui si vede che cosa esiste e che cosa va
 * dove.
 */
type Riga = ConsoleDashboard & { kind: DashboardKind }

const TIPI: { kind: DashboardKind; label: () => string; editor: string; parete: (id: string) => string }[] = [
  { kind: 'eagle', label: () => t('Cruscotto'), editor: '/eagle', parete: (id) => `/eagle/sinottico?kind=eagle&d=${id}` },
  // Wall displays saved before migration 0217 are cockpits now; a console
  // against an older Control Plane may still see one.
  { kind: 'eagle_wall', label: () => t('Cruscotto'), editor: '/eagle', parete: (id) => `/eagle/sinottico?kind=eagle_wall&d=${id}` },
  { kind: 'home', label: () => t('Home'), editor: '/', parete: (id) => `/sinottico?d=${id}` },
]

const tipoDi = (kind: DashboardKind) => TIPI.find((tp) => tp.kind === kind) ?? TIPI[0]

/** The «⋯» menu of a row: the actions that are not the two everyday ones. */
export function MenuAltro({ voci }: { voci: { label: string; onClick: () => void; pericolosa?: boolean }[] }) {
  const [aperto, setAperto] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setAperto(false)
    }
    document.addEventListener('mousedown', fuori)
    return () => document.removeEventListener('mousedown', fuori)
  }, [aperto])
  if (voci.length === 0) return null
  return (
    <span className="menu-azioni" ref={box}>
      <button type="button" className="secondary" aria-haspopup="menu" aria-expanded={aperto} aria-label={t('Altre azioni')}
              onClick={() => setAperto((v) => !v)}>⋯</button>
      {aperto && (
        <div className="menu-azioni__pannello" role="menu">
          {voci.map((v) => (
            <button key={v.label} type="button" role="menuitem" className={`menu-azioni__voce${v.pericolosa ? ' menu-azioni__voce--pericolosa' : ''}`}
                    onClick={() => { setAperto(false); v.onClick() }}>{v.label}</button>
          ))}
        </div>
      )}
    </span>
  )
}

export function GestioneCruscotti({ amministratore }: { amministratore: boolean }) {
  const navigate = useNavigate()
  // Every kind this person can read: a kind that answers 403 (a role without
  // the home dashboard) simply does not appear.
  const elenco = useAsync(async () => {
    const esiti = await Promise.all(TIPI.map((tp) =>
      api.dashboards(tp.kind).then((r) => r.dashboards.map((d): Riga => ({ ...d, kind: tp.kind })), (): Riga[] => [])))
    return esiti.flat()
  }, [])
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState<string | null>(null)

  async function azione(d: Riga, fn: () => Promise<unknown>) {
    setBusy(d.id ?? '')
    setErrore(null)
    try {
      await fn()
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(null)
    }
  }
  const corpo = (d: Riga, parte: { name?: string; shared?: boolean } = {}) => ({
    name: parte.name ?? d.name, kind: d.kind, shared: parte.shared ?? d.shared, sections: d.sections, rotation_s: d.rotation_s,
  })
  const rinomina = (d: Riga) => {
    const nome = prompt(t('Nuovo nome'), d.name)
    if (!nome || !nome.trim() || nome.trim() === d.name) return
    void azione(d, () => api.dashboardAggiorna(d.id!, corpo(d, { name: nome.trim() })))
  }
  const duplica = (d: Riga) => void azione(d, () => api.dashboardCrea({ ...corpo(d, { shared: false }), name: t('{name} (copia)', { name: d.name }) }))
  const condividi = (d: Riga) => void azione(d, () => api.dashboardAggiorna(d.id!, corpo(d, { shared: !d.shared })))
  const elimina = (d: Riga) => {
    if (!confirm(d.shared
      ? t('Eliminare «{name}»? È del tenant: sparisce per tutti, anche dai monitor che la mostrano.', { name: d.name })
      : t('Eliminare «{name}»?', { name: d.name }))) return
    void azione(d, () => api.dashboardElimina(d.id!, d.kind))
  }

  const righe = elenco.data ?? []
  const colonne: Column<Riga>[] = [
    { key: 'name', header: t('Nome'), render: (d) => <Link to={`${tipoDi(d.kind).editor}?d=${d.id}`}><strong>{d.name}</strong></Link> },
    { key: 'kind', header: t('Tipo'), value: (d) => tipoDi(d.kind).label(), render: (d) => <span className="gcru__tipo">{tipoDi(d.kind).label()}</span> },
    { key: 'chi', header: t('Di chi'), value: (d) => d.shared ? t('del tenant') : d.is_creator ? t('mia') : (d.created_by_name ?? t('personale')),
      render: (d) => d.shared ? <strong>{t('del tenant')}</strong> : d.is_creator ? t('mia') : <span className="muted">{d.created_by_name ?? t('personale')}</span> },
    { key: 'sezioni', header: t('Sezioni'), type: 'number', value: (d) => d.sections.length },
    { key: 'widget', header: t('Widget'), type: 'number', value: (d) => d.sections.reduce((n, s) => n + s.widgets.length, 0) },
    { key: 'updated_at', header: t('Modificata'), type: 'date', value: (d) => d.updated_at ?? '',
      render: (d) => d.updated_at ? <>{formatDateTime(d.updated_at)}{d.updated_by_name && <span className="muted"> · {d.updated_by_name}</span>}</> : '—' },
    { key: 'locked', header: t('Blocco'), value: (d) => d.locked ? t('bloccata') : '', render: (d) => d.locked ? <span className="muted">{t('bloccata')}</span> : '' },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (d) => {
      const tp = tipoDi(d.kind)
      const occupato = busy === d.id
      const modifica = d.can_edit !== false
      const altre = [
        { label: t('Duplica'), onClick: () => duplica(d) },
        ...(modifica ? [{ label: t('Rinomina'), onClick: () => rinomina(d) }] : []),
        ...(amministratore && modifica ? [{ label: d.shared ? t('Rendi mia') : t('Condividi con il tenant'), onClick: () => condividi(d) }] : []),
        ...(modifica ? [{ label: t('Elimina'), onClick: () => elimina(d), pericolosa: true }] : []),
      ]
      return (
        <span className="rowactions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <button type="button" className="secondary" onClick={() => navigate(`${tp.editor}?d=${d.id}`)} disabled={occupato}>{t('Apri')}</button>
          <button type="button" className="secondary" onClick={() => navigate(tp.parete(d.id!))} disabled={occupato}>{t('A parete')}</button>
          <MenuAltro voci={altre} />
        </span>
      )
    } },
  ]

  return (
    <>
      <Card title={t('Cruscotti')} actions={(
        <button type="button" onClick={() => navigate('/eagle')}>{t('Nuovo cruscotto')}</button>
      )}>
        <p className="field__hint">
          {t('Un cruscotto è la pagina di Eagle con i widget che scegli, anche su una matrice libera. «A parete» mostra a tutto schermo proprio quella riga; «Condividi con il tenant» la rende quella che vedono tutti e che i monitor mostrano se non ne hanno uno assegnato. Un cruscotto nuovo si crea dal cruscotto con «Nuovo» (vuoto, da comporre) o con «Personalizza» e «Salva come mio» (una copia di quello aperto); i monitor si assegnano in «Sinottici pubblicati».')}
        </p>
        <ErrorBanner error={errore ?? elenco.error} />
        {elenco.loading && !elenco.data ? <Spinner /> : righe.length === 0 ? (
          <EmptyState title={t('Nessun cruscotto salvato.')} hint={t('Il cruscotto predefinito si vede comunque: da «Personalizza» si salva come proprio o per il tenant, e compare qui.')} />
        ) : (
          <DataTable rows={righe} columns={colonne} rowKey={(d) => `${d.kind}:${d.id}`} conteggio={{ nome: t('cruscotti') }} />
        )}
      </Card>
      {amministratore && (
        <p className="field__hint">{t('I monitor a cui assegnare un cruscotto, con il loro link senza accesso, stanno in')} <Link to="/eagle/parete">{t('Sinottici pubblicati')}</Link>.</p>
      )}
    </>
  )
}
